import { inject, Injectable } from '@angular/core';
import { Environment } from '@rma/generic/util/environment';
import { FeatureFlags, TempFeatureFlags } from '@rma/generic/util/feature-flags';
import { combineLatest, map, Observable } from 'rxjs';
import { COLLECT_GROUP_FEATURES, getCollectGroupItems } from '../entities/nav-items-collect';
import {
  getManageGroupItems,
  getSettingsGroupItems,
  MANAGE_GROUP_FEATURES,
  SettingGroupFeature,
  SETTINGS_GROUP_FEATURES,
} from '../entities/nav-items-manage';
import { getOverview, getProfile, NavigationUserType } from '../entities/nav-items-misc';
import { getMonitorGroupItems, MonitorGroupFeature, MONITORING_GROUP_FEATURES } from '../entities/nav-items-monitor';
import { filterShareItems, getShareGroupItems, SHARE_GROUP_FEATURES } from '../entities/nav-items-share';
import { createNavigationGroup, NavigationGooglePermissions, NavigationGroup } from '../entities/side-nav-items';
import { SideNavigationConfigService } from './side-navigation-config.service';

@Injectable({
  providedIn: 'root',
})
export class OfficeSideMenuService {
  private readonly config = inject(SideNavigationConfigService);
  private readonly environment = inject(Environment);

  public getNavigation(): Observable<NavigationGroup[]> {
    return combineLatest({
      enableDatafeeds: this.config.getLiveFeature(FeatureFlags.EnableDataFeeds, false),
      enableOfficeAwards: this.config.getLiveFeature(FeatureFlags.EnableOfficeAwards, false),
      googlePermissions: this.config.getGoogleConfig(),
      hasLeasing: this.config.getLiveFeature(FeatureFlags.EnableLeasing, false),
      hasReviews: this.config.getSubscriptionAccess('HasReviewManagement'),
      hasSocialMedia: this.config.getSubscriptionAccess('HasSocialMedia'),
      marketReport: this.config.getMarketReportConfig(),
      showIntegrations: this.config.getLiveFeature(FeatureFlags.DashboardEnableIntegrations, false),
      showLeaderboards: this.config.getLiveFeature(FeatureFlags.DashboardEnableAgencyLeaderboard, false),
      showCsMenuItem: this.config.showSocialStudioLink$,
      displayBrandKits: this.config.getLiveFeature(TempFeatureFlags.BrandKitAdmin, false),
    }).pipe(
      map(
        ({
          enableDatafeeds,
          enableOfficeAwards,
          googlePermissions,
          hasLeasing,
          hasReviews,
          marketReport,
          showIntegrations,
          showLeaderboards,
          showCsMenuItem,
          displayBrandKits,
        }) => {
          const { baseUrl } = this.environment;
          const { hasMarketReportAccess, hideMarketReport } = marketReport;
          const userType: NavigationUserType = 'agency';
          return [
            createNavigationGroup([getProfile()]),
            createNavigationGroup([getOverview()]),
            this.getCollectGroup(userType, hasReviews, hasLeasing),
            this.getMonitorGroup(showLeaderboards),
            this.getManageItems(baseUrl, enableDatafeeds, displayBrandKits),
            this.getShareGroup(
              userType,
              showIntegrations,
              hideMarketReport,
              hasMarketReportAccess,
              enableOfficeAwards,
              showCsMenuItem,
              googlePermissions,
              baseUrl,
            ),
          ].filter((group) => group.items.length);
        },
      ),
    );
  }

  private getCollectGroup(userType: NavigationUserType, hasReviews: boolean, hasLeasing: boolean) {
    const notAvailable = hasLeasing ? ['pending'] : ['pending', 'leasing'];
    const features = COLLECT_GROUP_FEATURES.filter((f) => !notAvailable.includes(f) || !hasReviews);
    const items = getCollectGroupItems(features, userType);
    return createNavigationGroup(items, 'collect');
  }

  private getShareGroup(
    userType: NavigationUserType,
    showIntergations: boolean,
    hideMarketReport: boolean,
    hasMarketReportAccess: boolean,
    hasAwards: boolean,
    showCsMenuItem: boolean,
    googlePermissions: NavigationGooglePermissions,
    baseUrl: string,
  ): NavigationGroup {
    const features = SHARE_GROUP_FEATURES.filter(filterShareItems(showIntergations, hideMarketReport, hasAwards, showCsMenuItem));

    const items = getShareGroupItems(features, userType, hasMarketReportAccess, googlePermissions, baseUrl);

    return createNavigationGroup(items, 'share');
  }

  private getMonitorGroup(showLeaderboards: boolean): NavigationGroup {
    const notAvailable: MonitorGroupFeature[] = !showLeaderboards ? ['leaderboards'] : [];
    const features = MONITORING_GROUP_FEATURES.filter((f) => !notAvailable.includes(f));

    return createNavigationGroup(getMonitorGroupItems(features), 'monitor');
  }

  private getManageItems(baseUrl: string, enableDatafeeds: boolean, displayBrandKits: boolean): NavigationGroup {
    const features = MANAGE_GROUP_FEATURES.map((i) => i);

    return createNavigationGroup(
      [...getManageGroupItems(features), getSettingsGroupItems(this.getSettingsItems(enableDatafeeds, displayBrandKits), baseUrl)],
      'manage',
    );
  }

  private getSettingsItems(enableDatafeeds: boolean, displayBrandKits: boolean): SettingGroupFeature[] {
    const notAvailable: SettingGroupFeature[] = ['assistants'];

    if (!enableDatafeeds) {
      notAvailable.push('dataFeeds');
    }

    if (!displayBrandKits) {
      notAvailable.push('brandKits');
    }

    const items = SETTINGS_GROUP_FEATURES.filter((i) => !notAvailable.includes(i));
    return items;
  }
}
